/**
 * Master admin in-app notification inbox — bell icon in TopNav.
 * Polls /api/admin/notifications, shows an unread badge, and marks a
 * notification read only on explicit click/dismiss (never auto on open).
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Bell, X } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const POLL_MS = 30000;

const timeAgo = (iso) => {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

export const NotificationBell = ({ onNavigate }) => {
  const { apiFetch } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wrapperRef = useRef(null);

  const load = useCallback(() => {
    apiFetch("/admin/notifications")
      .then((res) => {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unread_count || 0);
      })
      .catch(() => {});
  }, [apiFetch]);

  useEffect(() => {
    load();
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const dismiss = async (id, e) => {
    e?.stopPropagation();
    try {
      await apiFetch(`/admin/notifications/${id}/read`, { method: "POST" });
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // non-critical
    }
  };

  const handleClick = (n) => {
    if (!n.read_at) dismiss(n.id);
    if (n.link_page) {
      onNavigate?.(n.link_page, n.link_id);
      setOpen(false);
    }
  };

  return (
    <div className="top-nav-avatar-wrapper" ref={wrapperRef} data-testid="notification-bell-wrapper">
      <button
        type="button"
        className="top-nav-icon-btn top-nav-notification-btn"
        title="Notifications"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid="notification-bell-btn"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="notification-badge" data-testid="notification-unread-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="notification-dropdown" role="menu" data-testid="notification-dropdown">
          <div className="notification-dropdown-header">
            <span>Notifications</span>
          </div>
          <div className="notification-dropdown-list">
            {notifications.length === 0 ? (
              <div className="notification-empty" data-testid="notification-empty-state">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className={`notification-item ${n.read_at ? "" : "notification-item-unread"}`}
                  onClick={() => handleClick(n)}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="notification-item-body">
                    <div className="notification-item-title">{n.title}</div>
                    {n.message && <div className="notification-item-message">{n.message}</div>}
                    <div className="notification-item-time">{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.read_at && (
                    <button
                      type="button"
                      className="notification-item-dismiss"
                      title="Mark as read"
                      onClick={(e) => dismiss(n.id, e)}
                      data-testid={`notification-dismiss-${n.id}`}
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
